import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "@/components/Logo";
import ScanForm from "@/components/ScanForm";
import MobileStickyBar from "@/components/MobileStickyBar";
import StickyHeader from "@/components/landing/StickyHeader";
import CitationGap from "@/components/landing/CitationGap";
import WhyMatters from "@/components/landing/WhyMatters";
import HowItWorks from "@/components/landing/HowItWorks";
import WhatYouGet from "@/components/landing/WhatYouGet";
import Credibility from "@/components/landing/Credibility";
import LandingFAQ from "@/components/landing/LandingFAQ";
import FinalCTA from "@/components/landing/FinalCTA";
import ProjectDisclaimer from "@/components/ProjectDisclaimer";
import { useToast } from "@/hooks/use-toast";
import type { ScanResult } from "@/lib/api/ai-visibility";
import type { PlatformVisibilityResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [showMobileBar, setShowMobileBar] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  // Show toast if redirected from methodology page
  useEffect(() => {
    if (location.state?.message) {
      toast({
        title: "Scan Required",
        description: location.state.message,
        variant: "default",
      });
      window.history.replaceState({}, document.title);
    }
  }, [location, toast]);

  const handleScanComplete = (
    result: ScanResult,
    platforms?: PlatformVisibilityResult,
    google?: GoogleSearchResult,
    url?: string
  ) => {
    navigate('/results', {
      state: {
        scanResult: result,
        platformResult: platforms || null,
        googleResult: google || null,
        websiteUrl: url
      }
    });
  };

  // Show mobile sticky bar when form is out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowMobileBar(!entry.isIntersecting),
      { threshold: 0.5 }
    );
    if (formRef.current) observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How accurate is the AILens scan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every scan runs real-time API calls to ChatGPT and Google Search. AI responses can vary. That's why we show the exact query and response as evidence."
        }
      },
      {
        "@type": "Question",
        "name": "How is AILens different from other SEO tools?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most SEO tools check Google rankings. AILens also checks whether ChatGPT recommends your brand, with 4 technical checks specific to AI crawlers."
        }
      },
      {
        "@type": "Question",
        "name": "What if I score low on AILens?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A low score means there's a gap between your Google rankings and your AI visibility. The scan includes specific recommendations to help you improve. In a field observation across a sample of 30 brands, this gap between search ranking and AI visibility showed up repeatedly. That was a sample scan, not a controlled study."
        }
      }
    ]
  };

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AILens",
    "url": "https://ai-lens.app",
    "description": "AI visibility scanning tool that checks how ChatGPT recommendations and Google rankings reflect your brand",
    "applicationCategory": "SEO Tool",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <Helmet>
        <title>AILens | AI Visibility Scanner</title>
        <meta name="description" content="Check if AI recommends your brand. Research-based AI visibility scanner measuring ChatGPT recommendations and Google rankings." />
        <link rel="canonical" href="https://ai-lens.app/" />
        <meta property="og:title" content="AILens | Check Your AI Visibility" />
        <meta property="og:description" content="Research-based AI visibility scanning. ChatGPT recommendations and Google rankings." />
        <meta property="og:image" content="https://ai-lens.app/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://ai-lens.app" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AILens | Check Your AI Visibility" />
        <meta name="twitter:description" content="Research-based AI visibility scanning. How does ChatGPT see your brand?" />
        <meta name="twitter:image" content="https://ai-lens.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <StickyHeader />

      <main className="min-h-screen bg-background bg-dot-grid">
        {/* HERO */}
        <section className="relative flex flex-col items-center justify-center px-4 pt-10 md:pt-16 pb-10">
          <div className="hero-glow absolute inset-0 pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-col items-center gap-4 w-full max-w-lg">
            <Logo />

            <div className="text-center space-y-2">
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight" style={{ letterSpacing: "-0.02em" }}>
                Your brand ranks on Google.
                <br />
                But does AI{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[hsl(280,85%,65%)] to-[hsl(199,89%,48%)] gradient-shimmer">
                  recommend you
                </span>
                ?
              </h1>
              <p className="text-muted-foreground text-lg">
                Scan how ChatGPT & Google see you.
              </p>
            </div>

            <div
              ref={formRef}
              className="w-full border-2 border-border bg-card p-6 mt-2"
              style={{ boxShadow: "5px 5px 0px 0px #000, 0 0 20px rgba(168, 85, 247, 0.1)" }}
            >
              <ScanForm onScanComplete={handleScanComplete} />
            </div>
          </div>
        </section>

        {/* CONTENT SECTIONS */}
        <CitationGap />
        <WhyMatters />
        <HowItWorks />
        <WhatYouGet />
        <Credibility />
        <LandingFAQ />
        <FinalCTA />

        {/* FOOTER */}
        <footer className="border-t border-border py-8 px-4 text-center">
          <p className="text-xs text-muted-foreground">
            AILens © 2026 ·{" "}
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            {" "}·{" "}
            <a href="/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
          </p>
          <ProjectDisclaimer />
        </footer>
      </main>

      <MobileStickyBar onScanClick={scrollToForm} isVisible={false} />
    </>
  );
};

export default HomePage;
